import { useAuth } from "@clerk/clerk-react";
import { Box, Button, Grid } from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachContactsContext } from "contexts/OutreachContacts";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { GmailThread } from "schemas/dashboard";
import { routes } from "schemas/routes";

import EmailThread from "components/EmailThread";
import EmailDraftComponentScheduled from "components/EmailThread/EmailDraft/scheduled";
import { TemplateContentType } from "features/Influencer/ContactList/schema";
import { useOutreachTable } from "features/Influencer/ContactList/useOutreachTable";
import PartnershipData from "features/Influencer/Discover/DetailedBrandView/components/PartnershipData";
import { getVerifiedPartnershipData } from "features/Influencer/Discover/DetailedBrandView/helpers";
import { BrandCardSource } from "features/Influencer/Discover/Results/schema";
import BrandTitle from "features/Influencer/Discover/Shared/BrandTitle";
import { fetcherAuth } from "utils/api";
import { isDemoUser } from "utils/demo";
import { accessToPartnershipData, contactReplied } from "utils/influencer";

import ContactInsights from "../../ContactRow/components/ContactInsights";
import { noThreadFound } from "../../helpers";
import ScheduledThreadPreviewSkeleton from "./skeleton";

const ScheduledThreadPreview = () => {
  const { setErrorAlert } = useContext(AlertContext);
  const { getToken } = useAuth();
  const { currentOrg } = useContext(OrganizationUserContext);
  const { outreachContactId, threadId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fromSend = searchParams.get("fromSend") === "true";
  const {
    fetchScheduledEmails,
    currentContact,
    fetchContact,
    currentContactScheduledEmails,
    currentContactScheduledEmailsLoading,
    currentContactLoading,
  } = useContext(OutreachContactsContext);
  const { openAttachment } = useOutreachTable();

  const [gmailThread, setGmailThread] = useState<GmailThread | undefined>();
  const [missingIntegration, setMissingIntegration] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const { subscription } = useContext(SubscriptionContext);

  const hasAccessToPartnershipData = accessToPartnershipData(
    currentOrg,
    subscription,
  );

  const isDemoAccount = isDemoUser(currentOrg);

  const bentoBrand =
    currentContact &&
    (currentContact.bentoBrand || {
      createdAt: currentContact.createdAt || 0,
      id: 0,
      brandName: currentContact.brand?.name || "",
      normalizedName: currentContact.brand?.name || "",
      instagramUsername: currentContact.brand?.instagramUsername,
      website: currentContact.brand?.website,
      compensationTypes: currentContact.brand?.compensationTypes,
      tags: "",
      latestOutreachTemplateId: null,
    });
  const verifiedPartnershipInfo = bentoBrand
    ? getVerifiedPartnershipData(bentoBrand)
    : [];
  const hasContactInsights =
    currentContact && currentContact?.bentoContact
      ? contactReplied(currentContact?.bentoContact) ||
        currentContact?.bentoContact?.lastSentToAt
      : false;
  const hasDataToExpand =
    hasAccessToPartnershipData &&
    (hasContactInsights || verifiedPartnershipInfo?.length > 0);

  const fetchEmailThread = async () => {
    if (!currentOrg) {
      return;
    }
    setLoading(true);
    setGmailThread(undefined);

    try {
      const { gmailThread } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/outreach-contacts/${outreachContactId}/threads/${threadId}`,
        "GET",
      );
      setGmailThread(gmailThread);
    } catch (error) {
      if (error?.message?.includes("Google integration")) {
        setMissingIntegration(true);
      } else {
        setErrorAlert(error);
      }
    } finally {
      setLoading(false);
    }
  };

  const alreadyFetchScheduled = currentContactScheduledEmails?.find(
    (x) => x.outreachContactId === Number(outreachContactId),
  );

  // If the email has already been scheduled, redirect to sent tab
  const evaluatedForRedirectRef = useRef<number | null>(null);

  useEffect(() => {
    evaluatedForRedirectRef.current = null;
  }, [outreachContactId]);

  useEffect(() => {
    if (expanded) setExpanded(false);
    if (currentOrg?.id && outreachContactId) {
      fetchContact(Number(outreachContactId));
    }

    let cancelled = false;
    if (outreachContactId) {
      const contactIdNum = Number(outreachContactId);
      const shouldEvaluateRedirect =
        fromSend && evaluatedForRedirectRef.current !== contactIdNum;

      if (!alreadyFetchScheduled) {
        const fetchPromise = fetchScheduledEmails(contactIdNum);
        if (shouldEvaluateRedirect) {
          fetchPromise.then((scheduledEmails) => {
            if (cancelled) return;
            evaluatedForRedirectRef.current = contactIdNum;
            const hasOutreach = (scheduledEmails ?? []).some(
              (e) =>
                e.outreachContactId === contactIdNum &&
                e.contentType === TemplateContentType.OUTREACH,
            );
            if (!hasOutreach) {
              navigate(`/${routes.inboxSent}/${outreachContactId}`, {
                replace: true,
              });
            } else {
              searchParams.delete("fromSend");
              setSearchParams(searchParams, { replace: true });
            }
          });
        }
      } else if (shouldEvaluateRedirect) {
        const hasOutreach = currentContactScheduledEmails.some(
          (e) =>
            e.outreachContactId === contactIdNum &&
            e.contentType === TemplateContentType.OUTREACH,
        );
        evaluatedForRedirectRef.current = contactIdNum;
        if (!hasOutreach) {
          navigate(`/${routes.inboxSent}/${outreachContactId}`, {
            replace: true,
          });
        } else {
          searchParams.delete("fromSend");
          setSearchParams(searchParams, { replace: true });
        }
      }
    }

    if (currentOrg?.id) {
      if (threadId) {
        fetchEmailThread();
      } else {
        setGmailThread(undefined);
      }
    }
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id, outreachContactId, threadId]);

  return loading ||
    currentContactLoading ||
    currentContactScheduledEmailsLoading ? (
    <ScheduledThreadPreviewSkeleton />
  ) : (
    <Box>
      {bentoBrand && (
        <Box sx={{ p: 3 }}>
          <Grid>
            <BrandTitle
              bentoBrand={bentoBrand}
              source={BrandCardSource.THREAD_BRAND}
              // If no contact or brand insight, just expand all brand info to fill the gap.
              expanded={expanded || !hasDataToExpand}
              hideWrongInfoButton={true}
            />
          </Grid>

          {expanded && (
            <Box sx={{ mt: 2 }}>
              <PartnershipData
                title={
                  isDemoAccount
                    ? "Why this brand was pitched?"
                    : "Brand Insights"
                }
                noDataHide
                bentoBrand={bentoBrand}
                showGrid
                gridProps={{
                  xs: 12,
                  md: isDemoAccount ? 12 : true,
                  sx: { mb: 1 },
                }}
              />
              {currentContact.bentoContact && (
                <ContactInsights
                  title={
                    isDemoAccount
                      ? "Why this contact was selected?"
                      : "Contact Insights"
                  }
                  bentoContact={currentContact.bentoContact}
                />
              )}
            </Box>
          )}

          {!expanded && hasDataToExpand && (
            <Button
              size="small"
              sx={{ textTransform: "none", fontSize: 12 }}
              onClick={() => setExpanded(true)}
            >
              learn more
            </Button>
          )}
        </Box>
      )}

      {missingIntegration && noThreadFound}

      {!missingIntegration && gmailThread && (
        <Box sx={{ maxWidth: "100%", overflowX: "auto", mb: 3 }}>
          <EmailThread
            messages={gmailThread.messages}
            openAttachment={openAttachment}
            showSubject={true}
          />
        </Box>
      )}
      <EmailDraftComponentScheduled
        gmailThread={gmailThread}
        setGmailThread={setGmailThread}
      />
    </Box>
  );
};

export default ScheduledThreadPreview;
